import { Chip, colors, Icon, Text, TextField, Title2 } from '@kk/ui';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useOutletContext } from 'react-router';

import { BandPill, ChildCell, ColumnHead, Status } from '@/components/bits';
import { LinkButton } from '@/components/bits';
import { deck } from '@/copy/deck';
import { placeholders } from '@/copy/placeholders';
import { useErrorMessage, useNewScore, useRoster } from '@/data/hooks';
import { bandName } from '@/lib/band';
import { ageLabel, completionPct } from '@/lib/age';
import type { ShellContext } from '@/shell/Shell';
import { portalColors } from '@/theme/portalTokens';
import type { OrgRosterInvite, OrgRosterRow } from '@/types/wire';

type Filter =
  | { kind: 'all' }
  | { kind: 'group'; id: string }
  | { kind: 'assignment'; id: string }
  | { kind: 'pending' };

const same = (a: Filter, b: Filter) =>
  a.kind === b.kind && ('id' in a && 'id' in b ? a.id === b.id : true);

function TasksDone({ row }: { row: OrgRosterRow }) {
  const pct = completionPct(row.tasks.done, row.tasks.total);
  if (pct === null)
    return (
      <Text variant="bodySm" tone="muted">
        {deck.roster.dash}
      </Text>
    );
  return (
    <div className="cell-inline">
      <Text variant="rowTitleSm" tone="ink">
        {deck.roster.pct(pct)}
      </Text>
      <Text variant="time" tone="muted">
        {deck.roster.ofTotal(row.tasks.done, row.tasks.total)}
      </Text>
    </div>
  );
}

function Score({ row }: { row: OrgRosterRow }) {
  const newScore = useNewScore();
  return row.scoreBand ? (
    <BandPill band={row.scoreBand} label={bandName(row.scoreBand, newScore)} />
  ) : (
    <Text variant="meta" tone="muted">
      {deck.roster.notShared}
    </Text>
  );
}

const Active = () => (
  <Text variant="labelStrong" tone="link">
    {deck.roster.active}
  </Text>
);
const WaitingOnParent = () => (
  <Text variant="labelStrong" style={{ color: portalColors.amber }}>
    {deck.roster.waitingOnParent}
  </Text>
);
const Dash = () => (
  <Text variant="bodySm" tone="muted">
    {deck.roster.dash}
  </Text>
);

export function RosterScreen() {
  const errorMessage = useErrorMessage();
  const { isPhone } = useOutletContext<ShellContext>();
  const roster = useRoster();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>({ kind: 'all' });

  const children = useMemo(() => roster.data?.children ?? [], [roster.data]);
  const invites = useMemo(() => roster.data?.invites ?? [], [roster.data]);

  const groupChips = useMemo(() => {
    const m = new Map<string, { name: string; n: number }>();
    for (const r of children)
      for (const g of r.groups) m.set(g.id, { name: g.name, n: (m.get(g.id)?.n ?? 0) + 1 });
    return [...m.entries()].map(([id, v]) => ({ id, ...v }));
  }, [children]);
  const assignmentChips = useMemo(() => {
    const m = new Map<string, { title: string; n: number }>();
    for (const r of children)
      for (const a of r.assignments) m.set(a.id, { title: a.title, n: (m.get(a.id)?.n ?? 0) + 1 });
    return [...m.entries()].map(([id, v]) => ({ id, ...v }));
  }, [children]);

  const q = search.trim().toLowerCase();
  const rows = children.filter((r) => {
    if (q && !r.child.firstName.toLowerCase().includes(q)) return false;
    if (filter.kind === 'group') return r.groups.some((g) => g.id === filter.id);
    if (filter.kind === 'assignment') return r.assignments.some((a) => a.id === filter.id);
    if (filter.kind === 'pending') return false;
    return true;
  });
  const inviteRows: OrgRosterInvite[] =
    !q && (filter.kind === 'all' || filter.kind === 'pending') ? invites : [];
  const groupName = (id: string) => groupChips.find((g) => g.id === id)?.name ?? '';

  /*
   * Three different answers to an empty table, and they must not share a string (Evan, 2026-09-16):
   * a club nobody has joined yet is told how families arrive; a filter or a search that matched
   * nothing says so and nothing else. "No families yet" under a search would read as if the search
   * had emptied the club.
   */
  const emptyState =
    rows.length + inviteRows.length > 0
      ? null
      : children.length + invites.length === 0
        ? 'none'
        : 'noMatches';
  const status = roster.isPending
    ? placeholders.empty.loading
    : roster.error
      ? errorMessage(roster.error)
      : emptyState === 'noMatches'
        ? deck.roster.noMatches
        : emptyState === 'none'
          ? deck.roster.emptyTitle
          : null;
  /** The second line the empty club gets, and the only state that has one. */
  const navigate = useNavigate();
  const statusBody = !roster.isPending && !roster.error && emptyState === 'none';

  const header = (
    <div className="page-header">
      <div className="page-header-text">
        <Title2 tone="ink">{deck.roster.title(children.length)}</Title2>
        <Text variant="bodySm" tone="muted">
          {deck.roster.subtitle}
        </Text>
      </div>
      <div className="actions">
        {/*
         * **O09's door, and the only one.** O05 promises it in as many words — "Children can still be invited
         * by email from the Roster" — and the board's O09 rail carries no nav item of its own, so a seventh
         * sidebar row would be a section the board does not draw. The label is the screen's own title rather
         * than a new string.
         */}
        <LinkButton
          label={deck.o09.title}
          onPress={() => navigate('/invite-families')}
          testID="invite-families"
        />
        <div className="actions-field">
          <TextField
            placeholder={deck.roster.searchPlaceholder}
            value={search}
            onChangeText={setSearch}
            testID="roster-search"
          />
        </div>
        {/*
          "Invite a family" was drawn here permanently disabled. Removed rather than enabled (Evan,
          2026-09-16: *"If it can't work yet, it should not be drawn; a permanently disabled primary
          button teaches people the portal is broken."*) — and it cannot work yet: no route creates an
          org_family invite. The roster already LISTS the pending ones, and enrollChild already
          accepts them, so the sender is the only piece missing (OPEN_QUESTIONS 51b).
        */}
      </div>
    </div>
  );

  const chips = (
    <div className="chips">
      <Chip
        label={deck.roster.chipAll(children.length)}
        selected={filter.kind === 'all'}
        onPress={() => setFilter({ kind: 'all' })}
      />
      {groupChips.map((g) => (
        <Chip
          key={g.id}
          label={deck.roster.chipCount(g.name, g.n)}
          selected={same(filter, { kind: 'group', id: g.id })}
          onPress={() => setFilter({ kind: 'group', id: g.id })}
        />
      ))}
      {assignmentChips.map((a) => (
        <Chip
          key={a.id}
          label={deck.roster.chipCount(a.title, a.n)}
          selected={same(filter, { kind: 'assignment', id: a.id })}
          onPress={() => setFilter({ kind: 'assignment', id: a.id })}
        />
      ))}
      {invites.length > 0 ? (
        <div className="chip-pending">
          <button
            type="button"
            className={`chip-pending-pill${filter.kind === 'pending' ? ' selected' : ''}`}
            aria-pressed={filter.kind === 'pending'}
            onClick={() => setFilter({ kind: 'pending' })}
          >
            <Text
              variant={filter.kind === 'pending' ? 'chipSelected' : 'chip'}
              tone={filter.kind === 'pending' ? 'white' : 'slate'}
            >
              {deck.roster.chipPending(invites.length)}
            </Text>
          </button>
        </div>
      ) : null}
    </div>
  );

  const lockNote = (
    <div className="lock-note">
      <Icon name="lock" size={18} color={colors.deepTeal} />
      <Text variant="meta" tone="slate" style={{ flex: 1 }}>
        {deck.roster.lockNote}
      </Text>
    </div>
  );

  if (isPhone)
    return (
      <>
        {header}
        {chips}
        {status ? (
          <Status>
            {status}
            {statusBody ? <> {deck.roster.emptyBody}</> : null}
          </Status>
        ) : null}
        {rows.map((r) => (
          <div className="card-row" key={r.child.id} data-testid={`roster-${r.child.id}`}>
            <div className="cell grow">
              <ChildCell child={r.child} size={40} />
              <Text variant="meta" tone="slate" numberOfLines={1}>
                {deck.roster.joinNames(r.groups.map((g) => g.name))}
              </Text>
              <div className="cell-inline">
                <TasksDone row={r} />
                <Score row={r} />
              </div>
            </div>
            <Active />
          </div>
        ))}
        {inviteRows.map((i) => (
          <div className="card-row invite" key={i.id}>
            <div className="dashed-disc" />
            <div className="cell grow">
              <Text variant="rowTitleSm" tone="ink" numberOfLines={1}>
                {deck.roster.invited(i.email)}
              </Text>
              <Text variant="meta" tone="slate" numberOfLines={1}>
                {deck.roster.joinNames(i.groupIds.map(groupName).filter(Boolean))}
              </Text>
            </div>
            <WaitingOnParent />
          </div>
        ))}
        {lockNote}
      </>
    );

  return (
    <>
      {header}
      {chips}
      <div className="table-card">
        <div className="table-scroll">
          <div className="grid-head grid-roster">
            <ColumnHead>{deck.roster.columns.child}</ColumnHead>
            <ColumnHead>{deck.roster.columns.ageBand}</ColumnHead>
            <ColumnHead>{deck.roster.columns.groups}</ColumnHead>
            <ColumnHead>{deck.roster.columns.tasksDone}</ColumnHead>
            <ColumnHead>{deck.roster.columns.score}</ColumnHead>
            <ColumnHead>{deck.roster.columns.consent}</ColumnHead>
          </div>
          {rows.map((r) => (
            <div
              className="grid-row grid-roster"
              key={r.child.id}
              data-testid={`roster-${r.child.id}`}
            >
              <ChildCell child={r.child} showAge={false} />
              <Text variant="bodySm" tone="ink">
                {ageLabel(r.child)}
              </Text>
              <Text variant="meta" tone="slate" numberOfLines={2}>
                {deck.roster.joinNames(r.groups.map((g) => g.name))}
              </Text>
              <TasksDone row={r} />
              <Score row={r} />
              <Active />
            </div>
          ))}
          {inviteRows.map((i) => (
            <div className="grid-row grid-roster invite" key={i.id} data-testid={`invite-${i.id}`}>
              <div className="child-cell">
                <div className="dashed-disc" />
                <Text variant="rowTitleSm" tone="ink" numberOfLines={1}>
                  {deck.roster.invited(i.email)}
                </Text>
              </div>
              <Dash />
              <Text variant="meta" tone="slate" numberOfLines={2}>
                {deck.roster.joinNames(i.groupIds.map(groupName).filter(Boolean))}
              </Text>
              <Dash />
              <Dash />
              <WaitingOnParent />
            </div>
          ))}
        </div>
        {status ? (
          <div className="grid-row grid-roster">
            <Status>
              {status}
              {statusBody ? <> {deck.roster.emptyBody}</> : null}
            </Status>
          </div>
        ) : null}
      </div>
      {lockNote}
    </>
  );
}
